import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useRef, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { useShowFeatureFlagsNav } from "../../hooks/useShowFeatureFlagsNav";
import { useUrlSort } from "../../hooks/useUrlSort";
import { routes } from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import { Box } from "../Box";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import {
  Container,
  FieldLabel,
  Header,
  HoverActions,
  ModalContent,
  ModalFooter,
  NeutralIconBadge,
  ScrollArea,
} from "./shared";

const HeaderActions = styled("div")`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleLabel = styled("div")`
  opacity: 0.3;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NameCell = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export function FeatureFlagsSettings() {
  const canManageFlags = useHasAccess("tenant:feature_flags_manage");
  const z = useZero();
  const [flags, flagsResult] = useQuery(queries.allFeatureFlags());
  const showInSidebar = useShowFeatureFlagsNav();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.settings.featureFlags,
    {
      name: "asc",
      createdBy: "desc",
      created: "desc",
    }
  );
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showAddModal) setTimeout(() => nameInputRef.current?.focus(), 0);
  }, [showAddModal]);

  const trimmed = newName.trim();
  const duplicate = flags.some((f) => f.name === trimmed);
  const canAdd = trimmed.length > 0 && !duplicate;

  const sortedFlags = useMemo(() => {
    const list = [...flags];
    list.sort((a, b) => {
      if (!sortColumn) return (b.createdAt ?? 0) - (a.createdAt ?? 0);

      let aVal: string | number;
      let bVal: string | number;

      switch (sortColumn) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "createdBy":
          aVal = a.createdBy ? userDisplayName(a.createdBy).toLowerCase() : "";
          bVal = b.createdBy ? userDisplayName(b.createdBy).toLowerCase() : "";
          break;
        case "created":
          aVal = a.createdAt ?? 0;
          bVal = b.createdAt ?? 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [flags, sortColumn, sortDirection]);

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewName("");
  };

  const handleAdd = () => {
    if (!canAdd) return;
    z.mutate(
      sharedMutators.createFeatureFlag({ id: nanoid(12), name: trimmed })
    );
    closeAddModal();
  };

  const items = sortedFlags.map((flag) => ({
    key: flag.id,
    name: (
      <NameCell>
        <div style={{ flexShrink: 0 }}>
          <NeutralIconBadge iconName="ticket" />
        </div>
        <Text variant="text-regular-md" color="var(--text-primary)" ellipsis>
          {flag.name}
        </Text>
      </NameCell>
    ),
    createdBy: flag.createdBy ? (
      <Text variant="text-small" ellipsis>
        {userDisplayName(flag.createdBy)}
      </Text>
    ) : (
      <Text variant="text-small" color="var(--text-tertiary)">
        –
      </Text>
    ),
    created: (
      <Text variant="text-small">
        {flag.createdAt
          ? new Date(flag.createdAt).toLocaleDateString(undefined, {
              dateStyle: "medium",
            })
          : null}
      </Text>
    ),
    state: (
      <Toggle
        isOn={flag.enabledAt != null}
        onToggle={() =>
          flag.enabledAt != null
            ? z.mutate(sharedMutators.disableFeatureFlag({ name: flag.name }))
            : z.mutate(
                sharedMutators.enableFeatureFlag({
                  id: flag.id,
                  name: flag.name,
                })
              )
        }
      />
    ),
    actions: (hasMouse: boolean) => (
      <HoverActions visible={hasMouse}>
        <Dropdown
          opener={<Button variant="secondary" icon="more" />}
          align="bottom"
          minWidth={160}
          sections={[
            {
              items: [
                {
                  type: "button" as const,
                  left: <Icon name="trash" size={16} />,
                  label: "Delete",
                  onClick: () =>
                    setConfirmDelete({ id: flag.id, name: flag.name }),
                },
              ],
            },
          ]}
        />
      </HoverActions>
    ),
  }));

  if (!canManageFlags) return null;

  return (
    <Container>
      <Header style={{ paddingRight: 12 }}>
        <Text variant="text-regular-md">Feature flags</Text>
        <HeaderActions>
          <ToggleLabel>
            <Text variant="text-small-md">Show in sidebar</Text>
          </ToggleLabel>
          <Toggle
            isOn={showInSidebar}
            onToggle={() =>
              z.mutate(
                sharedMutators.setUserPreference({
                  showFeatureFlagsNav: showInSidebar ? "off" : "on",
                })
              )
            }
          />
          <Icon name="slash" size={12} color="var(--border-alpha)" />
          <Button size="l" icon="plus" onClick={() => setShowAddModal(true)} />
        </HeaderActions>
      </Header>

      <ScrollArea>
        {flagsResult.type === "complete" && items.length === 0 ? (
          <EmptyState
            iconName="ticket"
            title="No feature flags"
            description="Turn features on or off in 3B without shipping a new build."
          />
        ) : (
          <ListTable
            headers={{
              name: "Name",
              createdBy: "Created by",
              created: "Created",
              state: "Enabled",
              actions: "",
            }}
            columns={{ name: "flex" }}
            sortableColumns={sortableColumns}
            dateColumns={["created"]}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            items={items}
          />
        )}
      </ScrollArea>

      <Modal open={showAddModal} onClose={closeAddModal}>
        <ModalContent>
          <Text variant="text-regular-md">Add feature flag</Text>
          <FieldLabel>
            <Box horizontal={8} vertical={10}>
              <Text variant="text-small-md">Name</Text>
            </Box>
            <Input
              ref={nameInputRef}
              placeholder="e.g. new-editor"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAdd();
              }}
              style={{ minWidth: 0, width: "100%" }}
            />
          </FieldLabel>
          <ModalFooter>
            <Button size="l" variant="secondary" onClick={closeAddModal}>
              Cancel
            </Button>
            <Button size="l" onClick={handleAdd} disabled={!canAdd}>
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            z.mutate(
              sharedMutators.deleteFeatureFlag({ id: confirmDelete.id })
            );
            setConfirmDelete(null);
          }}
          title="Delete feature flag"
          description={`“${confirmDelete.name}” will be permanently deleted.`}
          confirmLabel="Delete"
          destructive
        />
      )}
    </Container>
  );
}
